/**
 * mapProvider — single source of truth for Leaflet tile layers.
 *
 * The admin picks a provider in Settings → Integrations → Maps. Any map view
 * in the app calls `addProviderTileLayer(L, map)` instead of hard-coding an
 * OSM tile URL. Config is cached in-memory (5 min) so a page with 3 maps
 * doesn't hit the backend 3 times.
 *
 * If the fetch fails (e.g. offline, endpoint 500) we fall back to OSM so
 * maps still render.
 */
import api from "@/api/client";

let _cache = null;
let _cacheAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000;

const OSM_FALLBACK = {
  provider: "osm",
  tile_url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  subdomains: ["a", "b", "c"],
  max_zoom: 19,
  attribution: "© OpenStreetMap",
};

export async function getMapProviderConfig({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cache && now - _cacheAt < CACHE_TTL_MS) return _cache;
  try {
    const r = await api.get("/map-settings/public");
    _cache = r.data || OSM_FALLBACK;
  } catch {
    _cache = OSM_FALLBACK;
  }
  _cacheAt = now;
  return _cache;
}

export function invalidateMapProviderCache() {
  _cache = null;
  _cacheAt = 0;
}

/**
 * Create + add the configured tile layer to a Leaflet map instance.
 * Returns the created L.TileLayer, or null if the map was removed before
 * the config finished loading (StrictMode double-mount / rapid unmount).
 */
export async function addProviderTileLayer(L, map) {
  const cfg = await getMapProviderConfig();
  // Map may have been removed before we resolved. Leaflet clears `_container`
  // on remove(); guard so we never call addTo on a dead map.
  if (!map || !map._container) return null;
  const opts = {
    attribution: cfg.attribution || "",
    maxZoom: cfg.max_zoom || 19,
  };
  if (cfg.subdomains) opts.subdomains = cfg.subdomains;
  if (cfg.tile_size) opts.tileSize = cfg.tile_size;
  if (typeof cfg.zoom_offset === "number") opts.zoomOffset = cfg.zoom_offset;
  const layer = L.tileLayer(cfg.tile_url, opts);
  try {
    layer.addTo(map);
  } catch {
    return null;
  }
  return layer;
}
